import dayjs from "dayjs";
import { Fragment, ReactNode, useCallback, useState } from "react";
import { useServerSdk } from "../RootNavigator/services/ServerSdk";
import Button from "@hpo/client/components/Button";

import List, { ListItem } from "@hpo/client/utilities/List";
import { Archive, ArchivePrefill } from "@hpo/client/models/Archive";
import SideBar from "@hpo/client/components/SideBar";
import T from "@hpo/client/components/text/Text";
import { PreviewView } from "@hpo/client/utilities/Preview";
import Spacer from "@hpo/client/utilities/Spacer";
import Intersperse from "@hpo/client/components/Intersperse";
import Toasting from "@hpo/client/components/Toasting";
import Divider from "@hpo/client/components/Divider";
import MessageException from "@hpo/client/utilities/errors/MessageException";
import TextField from "@hpo/client/utilities/fields/TextField";
import { useSubmitCallback } from "@hpo/client/utilities/useSubmitCallback";
import ErrorToast from "@hpo/client/utilities/ErrorToast";

type ArchiveListProps = {
  onEditParams: (params: ArchivePrefill) => unknown;
};

export default function ArchiveList(props: ArchiveListProps) {
  const { onEditParams } = props;
  const server = useServerSdk();

  const archives = server.suspender.use(server.getArchives());
  const [selected, setSelected] = useState<string | null>(null);

  const onSelect = useCallback(
    (archive: string | null) => {
      if (selected) {
        setSelected(null);
        setTimeout(() => setSelected(archive), 300);
      } else {
        setSelected(archive);
      }
    },
    [selected],
  );

  const onTemplate = useCallback(async (archive: Archive) => {
    if (!archive.params) return;
    onEditParams({ params: archive.params, group: archive.group });
    Toasting.success(
      "Le rapport sélectionné a été utilisé pour préremplir le formulaire",
    );
  }, []);

  return (
    <List
      data={archives}
      renderItem={(y) => (
        <Fragment>
          <ListItem
            label={y.title}
            onClick={() => setSelected(y.id)}
            help={[
              `Rédigé le ${dayjs(y.createdAt).format("LL")} par ${y.author.name}`,
            ]}
            right={
              <Fragment>
                <Button href={y.report} icon="eye" style="discreet" />
              </Fragment>
            }
          />
          <SideBar visible={selected === y.id} onHide={() => setSelected(null)}>
            <ArchiveDetails
              archive={y}
              onSelect={(a) => onSelect(a)}
              onTemplate={() => onTemplate(y)}
            />
          </SideBar>
        </Fragment>
      )}
    />
  );
}

type ArchiveDetailsProps = {
  archive: Archive;
  onSelect: (id: string | null) => unknown;
  onTemplate: () => unknown;
};

function ArchiveDetails(props: ArchiveDetailsProps) {
  const { archive, onSelect, onTemplate } = props;

  const server = useServerSdk();

  const [updateTitle, setUpdateTitle] = useState<string | null>(null);

  const [onRefresh, refreshing] = useSubmitCallback(async () => {
    if (archive.params === null) return;
    if (updateTitle === null)
      throw new MessageException("Saisissez un titre", null);
    const newVersion = await server.buildArchive(
      { title: updateTitle, sections: archive.params.sections },
      archive.group,
    );
    Toasting.success("Le rapport a été mis à jour");
    onSelect(newVersion);
    setUpdateTitle(null);
  }, [updateTitle, archive, onSelect]);

  const onDelete = useCallback(
    async (id: string) => {
      if (id === archive.id) {
        const hasHistory = 0 in archive.history;
        if (hasHistory) {
          const versionToOpen = archive.history[0].id;
          await server.deleteArchive(id);
          onSelect(versionToOpen);
        } else {
          onSelect(null);
          await server.deleteArchive(id);
        }
      } else {
        await server.deleteArchive(id);
      }
    },
    [archive, onSelect],
  );

  let updateNode: ReactNode;
  if (archive.params) {
    updateNode = (
      <Fragment>
        <T style="subtitle">Mettre à jour ce rapport</T>
        <Spacer />
        <T>
          Ce rapport à été produit le {dayjs(archive.createdAt).format("LLLL")}{" "}
          sur la base des données qui étaient présentes dans la plateforme à
          cette date. Vous pouvez mettre à jour ce rapport avec les données
          actuellement présentes dans la plateforme
        </T>
        <Spacer />
        <TextField
          required
          label="Titre de la nouvelle version"
          value={updateTitle}
          onChange={setUpdateTitle}
        />
        <Spacer />
        <Button label="Mettre à jour" onClick={onRefresh} />
      </Fragment>
    );
  }

  let editNode: ReactNode;
  if (archive.params) {
    editNode = (
      <Fragment>
        <T style="subtitle">Utiliser comme modèle</T>
        <Spacer />
        <T>
          Vous pouvez utiliser ce rapport comme modèle pour en produire un
          nouveau. Cela vous permettra de préremplir les sections de ce rapport
          avec les mêmes paramètres.
        </T>
        <Spacer />
        <Button label="Utilser comme modèle" onClick={onTemplate} />
      </Fragment>
    );
  }

  const historyNode: ReactNode = (
    <Fragment>
      <T style="subtitle">Versions</T>
      <br />
      <List
        data={[archive, ...archive.history]}
        renderItem={(y, i) => (
          <ListItem
            label={y.title}
            help={[
              i === 0 ? `Version actuelle` : null,
              `📅 Rédigé le ${dayjs(y.createdAt).format("LLLL")}`,
              `👤 Rédigée le  par ${y.author.name}`,
            ]}
            right={
              <Fragment>
                <Button href={y.report} icon={"eye"} />
                <Spacer horizontal />
                <Button
                  onClick={() => onDelete(y.id)}
                  icon={"trash"}
                  style="danger"
                />
              </Fragment>
            }
          />
        )}
      />
    </Fragment>
  );

  return (
    <Fragment>
      <ErrorToast error={refreshing.error} />
      <Intersperse between={() => <Spacer />}>
        <T style="section">{archive.title}</T>
        <Intersperse
          between={() => (
            <Fragment>
              <Spacer />
              <Divider />
              <Spacer />
            </Fragment>
          )}
        >
          <PreviewView file={archive.report} size={500} />
          {historyNode}
          {updateNode}
          {editNode}
          <Button
            label="Supprimer"
            onClick={() => onDelete(archive.id)}
            style="danger"
          />
        </Intersperse>
      </Intersperse>
    </Fragment>
  );
}
